import { canonicalSources, normalizeBrandText, type BrandSourceTag } from '@mg/shared';

/**
 * The parts of a brand field that BrandFieldShell draws around its control, and the rules both the
 * shell and the wizard's and Edit page's validity checks share. Pure.
 */

/** The ids of a field's parts, from its control's id. */
export function fieldPartIds(id: string) {
  return {
    /** The label, for a group control (a radio group) that is labelled by it. */
    label: `${id}-label`,
    hint: `${id}-hint`,
    sources: `${id}-sources`,
    error: `${id}-error`,
    counter: `${id}-counter`,
  };
}

/** Whether a field shows source tags: content from somewhere, or an AI suggestion (spec:109, :124). */
export function hasSourceTags(sources: readonly BrandSourceTag[], aiSuggested = false): boolean {
  return aiSuggested || canonicalSources(sources).length > 0;
}

export interface FieldDescription {
  hint?: string | null;
  sources?: readonly BrandSourceTag[];
  aiSuggested?: boolean;
  error?: string | null;
  counter?: string | null;
}

/**
 * The control's `aria-describedby`: the hint, the source tags, and the error (or else the counter),
 * only those the shell draws, so no id points at nothing.
 */
export function brandFieldDescribedBy(id: string, parts: FieldDescription): string | undefined {
  const ids = fieldPartIds(id);
  const out: string[] = [];
  if (parts.hint) out.push(ids.hint);
  if (hasSourceTags(parts.sources ?? [], parts.aiSuggested)) out.push(ids.sources);
  if (parts.error) out.push(ids.error);
  else if (parts.counter) out.push(ids.counter);
  return out.length > 0 ? out.join(' ') : undefined;
}

/** 2000 → "2,000", the same in every browser (no toLocaleString). */
export function formatCount(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** The counter shows from this share of a text field's limit. */
export const COUNTER_FROM_SHARE = 0.8;

/** The length the server counts: the text as it would be stored (trimmed, CR LF as LF). */
export function storedTextLength(value: string | null | undefined): number {
  return normalizeBrandText(value)?.length ?? 0;
}

/** "Shorten this to 2,000 characters or fewer." when the text is over its limit; else null. */
export function textLimitError(value: string | null | undefined, maxLength: number): string | null {
  return storedTextLength(value) > maxLength ? `Shorten this to ${formatCount(maxLength)} characters or fewer.` : null;
}

/** "1,650 of 2,000 characters" from 80% of the limit up to it; else null. */
export function textCounter(value: string | null | undefined, maxLength: number): string | null {
  const length = storedTextLength(value);
  if (maxLength <= 0 || length < maxLength * COUNTER_FROM_SHARE || length > maxLength) return null;
  return `${formatCount(length)} of ${formatCount(maxLength)} characters`;
}
